import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  IconButton,
  Chip,
  LinearProgress,
  Fab,
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  useTheme,
  useMediaQuery,
  AppBar,
  Toolbar,
  Avatar,
  Badge,
} from '@mui/material';
import {
  Dashboard,
  Engineering,
  Analytics,
  Warning,
  CheckCircle,
  Error,
  Refresh,
  Menu,
  Close,
  Add,
  FilterList,
  Search,
  TrendingUp,
  Speed,
  Thermostat,
  Vibration,
  Compress,
  CloudSync,
  Security,
} from '@mui/icons-material';
import ThreeJSAssetViewer from './ThreeJSAssetViewer';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

interface AssetData {
  id: string;
  name: string;
  type: string;
  status: 'healthy' | 'warning' | 'critical';
  position: [number, number, number];
  rotation: [number, number, number];
  scale: [number, number, number];
  health: number;
  temperature: number;
  vibration: number;
  pressure: number;
  lastUpdate: string;
  location: string;
  department: string;
}

interface AssetManagementDashboardProps {
  user: any;
}

const AssetManagementDashboard: React.FC<AssetManagementDashboardProps> = ({ user }) => {
  const [assets, setAssets] = useState<AssetData[]>([]);
  const [selectedAsset, setSelectedAsset] = useState<string | undefined>();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [realTimeData, setRealTimeData] = useState<any[]>([]);
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Sample data - in real app, this would come from API
  useEffect(() => {
    const sampleAssets: AssetData[] = [
      {
        id: '1',
        name: 'Main Pump A',
        type: 'pump',
        status: 'healthy',
        position: [0, 0, 0],
        rotation: [0, 0, 0],
        scale: [1, 1, 1],
        health: 95,
        temperature: 45,
        vibration: 2.1,
        pressure: 15.2,
        lastUpdate: '2 min ago',
        location: 'Building A',
        department: 'Production',
      },
      {
        id: '2',
        name: 'Motor Unit B',
        type: 'motor',
        status: 'warning',
        position: [2, 0, 0],
        rotation: [0, 0, 0],
        scale: [1, 1, 1],
        health: 78,
        temperature: 67,
        vibration: 4.8,
        pressure: 12.1,
        lastUpdate: '1 min ago',
        location: 'Building B',
        department: 'Maintenance',
      },
      {
        id: '3',
        name: 'Control Valve C',
        type: 'valve',
        status: 'critical',
        position: [-2, 0, 0],
        rotation: [0, 0, 0],
        scale: [1, 1, 1],
        health: 45,
        temperature: 89,
        vibration: 7.2,
        pressure: 8.5,
        lastUpdate: '30 sec ago',
        location: 'Building A',
        department: 'Production',
      },
      {
        id: '4',
        name: 'Sensor Array D',
        type: 'sensor',
        status: 'healthy',
        position: [0, 2, 0],
        rotation: [0, 0, 0],
        scale: [1, 1, 1],
        health: 92,
        temperature: 38,
        vibration: 1.5,
        pressure: 14.8,
        lastUpdate: '3 min ago',
        location: 'Building C',
        department: 'Quality',
      },
      {
        id: '5',
        name: 'Storage Tank E',
        type: 'tank',
        status: 'healthy',
        position: [0, -2, 0],
        rotation: [0, 0, 0],
        scale: [1, 1, 1],
        health: 88,
        temperature: 42,
        vibration: 0.8,
        pressure: 18.3,
        lastUpdate: '5 min ago',
        location: 'Building A',
        department: 'Storage',
      },
    ];

    setAssets(sampleAssets);
    setIsLoading(false);

    // Simulate real-time data updates
    const interval = setInterval(() => {
      setAssets(prevAssets => 
        prevAssets.map(asset => ({
          ...asset,
          health: Math.max(0, Math.min(100, asset.health + (Math.random() - 0.5) * 2)),
          temperature: Math.max(20, Math.min(100, asset.temperature + (Math.random() - 0.5) * 2)),
          vibration: Math.max(0, Math.min(10, asset.vibration + (Math.random() - 0.5) * 0.5)),
          pressure: Math.max(5, Math.min(25, asset.pressure + (Math.random() - 0.5) * 0.5)),
          lastUpdate: 'Just now',
        }))
      );
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'healthy': return '#34C759';
      case 'warning': return '#FF9500';
      case 'critical': return '#FF3B30';
      default: return '#8E8E93';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'healthy': return <CheckCircle sx={{ color: '#34C759' }} />;
      case 'warning': return <Warning sx={{ color: '#FF9500' }} />;
      case 'critical': return <Error sx={{ color: '#FF3B30' }} />;
      default: return <CheckCircle sx={{ color: '#8E8E93' }} />;
    }
  };

  const healthyAssets = assets.filter(asset => asset.status === 'healthy').length;
  const warningAssets = assets.filter(asset => asset.status === 'warning').length;
  const criticalAssets = assets.filter(asset => asset.status === 'critical').length;

  const chartData = [
    { name: 'Healthy', value: healthyAssets, color: '#34C759' },
    { name: 'Warning', value: warningAssets, color: '#FF9500' },
    { name: 'Critical', value: criticalAssets, color: '#FF3B30' },
  ];

  const timeSeriesData = assets.map(asset => ({
    name: asset.name,
    health: asset.health,
    temperature: asset.temperature,
    vibration: asset.vibration,
  }));

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#000000', color: '#ffffff' }}>
      {/* Mobile App Bar */}
      {isMobile && (
        <AppBar
          position="fixed"
          sx={{
            bgcolor: 'rgba(0, 0, 0, 0.9)',
            backdropFilter: 'blur(20px)',
            borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          }}
        >
          <Toolbar>
            <IconButton
              onClick={() => setMobileMenuOpen(true)}
              sx={{ color: '#ffffff', mr: 2 }}
            >
              <Menu />
            </IconButton>
            <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 600 }}>
              Asset Management
            </Typography>
            <IconButton sx={{ color: '#ffffff' }}>
              <Refresh />
            </IconButton>
          </Toolbar>
        </AppBar>
      )}

      {/* Mobile Menu Drawer */}
      <Drawer
        anchor="left"
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        sx={{
          '& .MuiDrawer-paper': {
            bgcolor: '#000000',
            width: 280,
            borderRight: '1px solid rgba(255, 255, 255, 0.1)',
          },
        }}
      >
        <Box sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#ffffff' }}>
              Menu
            </Typography>
            <IconButton onClick={() => setMobileMenuOpen(false)} sx={{ color: '#ffffff' }}>
              <Close />
            </IconButton>
          </Box>
          <List>
            {['Dashboard', 'Assets', 'Analytics', 'Alerts', 'Reports', 'Settings'].map((item) => (
              <ListItem key={item} sx={{ px: 0, py: 1 }}>
                <ListItemIcon>
                  <Dashboard sx={{ color: '#ffffff' }} />
                </ListItemIcon>
                <ListItemText
                  primary={item}
                  sx={{
                    '& .MuiListItemText-primary': {
                      color: '#ffffff',
                      fontSize: '1rem',
                      fontWeight: 400,
                    },
                  }}
                />
              </ListItem>
            ))}
          </List>
        </Box>
      </Drawer>

      {/* Main Content */}
      <Box sx={{ pt: isMobile ? '80px' : 0, p: isMobile ? 2 : 4 }}>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              mb: 2,
              fontSize: { xs: '1.5rem', md: '2rem' },
            }}
          >
            Digital Twin 4.0 Asset Management
          </Typography>
          <Typography variant="body1" sx={{ color: '#a1a1aa', mb: 3 }}>
            Real-time monitoring and management of industrial assets
          </Typography>
        </Box>

        {/* Stats Cards */}
        <Grid container spacing={2} sx={{ mb: 4 }}>
          <Grid item xs={6} md={3}>
            <Card sx={{ bgcolor: '#111111', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
              <CardContent sx={{ textAlign: 'center', p: 2 }}>
                <CheckCircle sx={{ color: '#34C759', fontSize: 32, mb: 1 }} />
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#34C759' }}>
                  {healthyAssets}
                </Typography>
                <Typography variant="body2" sx={{ color: '#a1a1aa' }}>
                  Healthy Assets
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={6} md={3}>
            <Card sx={{ bgcolor: '#111111', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
              <CardContent sx={{ textAlign: 'center', p: 2 }}>
                <Warning sx={{ color: '#FF9500', fontSize: 32, mb: 1 }} />
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#FF9500' }}>
                  {warningAssets}
                </Typography>
                <Typography variant="body2" sx={{ color: '#a1a1aa' }}>
                  Warning Assets
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={6} md={3}>
            <Card sx={{ bgcolor: '#111111', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
              <CardContent sx={{ textAlign: 'center', p: 2 }}>
                <Error sx={{ color: '#FF3B30', fontSize: 32, mb: 1 }} />
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#FF3B30' }}>
                  {criticalAssets}
                </Typography>
                <Typography variant="body2" sx={{ color: '#a1a1aa' }}>
                  Critical Assets
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={6} md={3}>
            <Card sx={{ bgcolor: '#111111', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
              <CardContent sx={{ textAlign: 'center', p: 2 }}>
                <Engineering sx={{ color: '#007AFF', fontSize: 32, mb: 1 }} />
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#007AFF' }}>
                  {assets.length}
                </Typography>
                <Typography variant="body2" sx={{ color: '#a1a1aa' }}>
                  Total Assets
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* 3D Asset Viewer */}
        <Card sx={{ bgcolor: '#111111', border: '1px solid rgba(255, 255, 255, 0.1)', mb: 4 }}>
          <CardContent sx={{ p: 0 }}>
            <Box sx={{ p: 2, borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                3D Asset Visualization
              </Typography>
            </Box>
            <ThreeJSAssetViewer
              assets={assets}
              selectedAsset={selectedAsset}
              onAssetSelect={setSelectedAsset}
              isMobile={isMobile}
            />
          </CardContent>
        </Card>

        {/* Asset List and Analytics */}
        <Grid container spacing={3}>
          {/* Asset List */}
          <Grid item xs={12} md={6}>
            <Card sx={{ bgcolor: '#111111', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                  Asset Status
                </Typography>
                <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                  {assets.map((asset) => (
                    <Box
                      key={asset.id}
                      onClick={() => setSelectedAsset(asset.id)}
                      sx={{
                        p: 2,
                        mb: 1,
                        borderRadius: 1,
                        bgcolor: selectedAsset === asset.id ? 'rgba(0, 122, 255, 0.2)' : 'transparent',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          bgcolor: 'rgba(255, 255, 255, 0.05)',
                        },
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          {asset.name}
                        </Typography>
                        {getStatusIcon(asset.status)}
                      </Box>
                      
                      <Box sx={{ display: 'flex', gap: 2, mb: 1, flexWrap: 'wrap' }}>
                        <Chip
                          label={asset.type.toUpperCase()}
                          size="small"
                          sx={{ bgcolor: 'rgba(0, 122, 255, 0.2)', color: '#007AFF' }}
                        />
                        <Chip
                          label={asset.location}
                          size="small"
                          sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)', color: '#ffffff' }}
                        />
                      </Box>
                      
                      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <TrendingUp sx={{ fontSize: 16, color: '#34C759' }} />
                          <Typography variant="body2" sx={{ color: '#a1a1aa' }}>
                            Health: {asset.health}%
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Thermostat sx={{ fontSize: 16, color: '#FF9500' }} />
                          <Typography variant="body2" sx={{ color: '#a1a1aa' }}>
                            {asset.temperature}°C
                          </Typography>
                        </Box>
                      </Box>
                      
                      <LinearProgress
                        variant="determinate"
                        value={asset.health}
                        sx={{
                          mt: 1,
                          height: 4,
                          borderRadius: 2,
                          bgcolor: 'rgba(255, 255, 255, 0.1)',
                          '& .MuiLinearProgress-bar': {
                            bgcolor: getStatusColor(asset.status),
                          },
                        }}
                      />
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Analytics Charts */}
          <Grid item xs={12} md={6}>
            <Card sx={{ bgcolor: '#111111', border: '1px solid rgba(255, 255, 255, 0.1)', mb: 3 }}>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                  Asset Health Distribution
                </Typography>
                <Box sx={{ height: 200 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={chartData}
                        cx="50%"
                        cy="50%"
                        innerRadius={40}
                        outerRadius={80}
                        dataKey="value"
                      >
                        {chartData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </Box>
              </CardContent>
            </Card>

            <Card sx={{ bgcolor: '#111111', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                  Performance Trends
                </Typography>
                <Box sx={{ height: 200 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={timeSeriesData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                      <XAxis dataKey="name" stroke="#a1a1aa" />
                      <YAxis stroke="#a1a1aa" />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: '#000000',
                          border: '1px solid rgba(255, 255, 255, 0.1)',
                          borderRadius: 8,
                        }}
                      />
                      <Line type="monotone" dataKey="health" stroke="#34C759" strokeWidth={2} />
                      <Line type="monotone" dataKey="temperature" stroke="#FF9500" strokeWidth={2} />
                    </LineChart>
                  </ResponsiveContainer>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      {/* Floating Action Button (Mobile) */}
      {isMobile && (
        <Fab
          sx={{
            position: 'fixed',
            bottom: 16,
            right: 16,
            bgcolor: '#007AFF',
            '&:hover': { bgcolor: '#0056CC' },
          }}
        >
          <Add />
        </Fab>
      )}
    </Box>
  );
};

export default AssetManagementDashboard;
